import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  VStack,
  Text,
  IconButton,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp, FaSearch } from "react-icons/fa";

import { motion, AnimatePresence } from "framer-motion";
import { useChatContext } from "../../../context/chatContext";
import { useUnreadStore } from "../store/useOrderStore";
import { useGetAllSuppliers } from "../../Orders/queryHooks";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { ChatType } from "../../../types/chatTypes";
import { ChatInboxConfig } from "../../../types/chatConfigs";
import { extractFromUniqueName } from "../../../utils/chatUtils";

const MotionBox = motion(Box);

interface ChatItem {
  orderNo: string;
  supplierName: string;
  customerName: string;
  uniqueName: string;
  hasUnread: boolean;
  orderHandlerUserId: string;
  supplierId: string;
  customerId: string;
  chatType: ChatType;
  unreadCount: number;
  lastUpdated: number;
  lastMessageDate?: Date;
  lastMessage?: string;
}

interface ChatInboxProps {
  config: ChatInboxConfig;
}

export const ChatInbox: React.FC<ChatInboxProps> = ({ config }) => {
  const { isConnected } = useChatContext();
  const conversations = useUnreadStore((s) => s.conversations);
  const [isMaximized, setIsMaximized] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const unreadCounts = useUnreadStore((s) => s.counts);
  const { data: allSuppliers } = useGetAllSuppliers();
  const userDetails = useUserDetailsContext();
  const supplierMap = useMemo(
    () =>
      new Map<string, any>(
        (allSuppliers ?? []).map((supplier: any) => [supplier._id, supplier])
      ),
    [allSuppliers]
  );

  const getSupplierBusinessNameById = useCallback(
    (supplierId?: string) => {
      const supplier = supplierMap.get(supplierId ?? "");
      return supplier?.businessName ?? "Supplier";
    },
    [supplierMap]
  );

  const chatStyles = {
    ...config.styles,
    ...config.content,
    emptyMessage: searchQuery
      ? `No ${config.content.chatTypeLabel.toLowerCase()} orders found`
      : config.content.emptyMessage,
  };

  const [chats, setChats] = React.useState<ChatItem[]>([]);

  // Unread visibility and animation scoped to this inbox's chatType
  const totalUnreadForType = useMemo(() => {
    return chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);
  }, [chats]);

  const hasAnyUnreadForType = totalUnreadForType > 0;
  const prevUnreadRef = useRef<number>(0);
  const [shouldShake, setShouldShake] = useState(false);

  useEffect(() => {
    const prev = prevUnreadRef.current;
    if (totalUnreadForType > prev) {
      setShouldShake(true);
      const t = setTimeout(() => setShouldShake(false), 600);
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = totalUnreadForType;
  }, [totalUnreadForType]);

  React.useEffect(() => {
    const loadChats = async () => {
      if (!conversations?.length) {
        setChats([]);
        return;
      }

      const chatPromises = conversations.map(async (conv: any) => {
        try {
          const meta = extractFromUniqueName(conv.uniqueName);
          if (!meta) return null;

          const { orderNo, supplierId, customerId, chatType } = meta;
          if (chatType !== config.chatType) return null;

          const orderHandlerUserId = userDetails?._id;
          const unreadCount = unreadCounts.get(conv.uniqueName) ?? 0;
          const lastMessage = conv.lastMessage ?? null;
          const lastUpdated =
            lastMessage?.dateCreated?.getTime?.() ??
            conv?.dateUpdated?.getTime?.() ??
            0;
          const lastMessageDate = lastMessage?.dateCreated;

          const participantName = await config.behavior.getParticipantName({
            chatType,
            supplierId,
            conversation: conv,
          });

          const supplierNameResolved =
            chatType === ChatType.SUPPLIER
              ? participantName || getSupplierBusinessNameById(supplierId)
              : "Unknown";
          const customerNameResolved =
            chatType === ChatType.CUSTOMER
              ? participantName || "Customer"
              : "Unknown";

          return {
            orderNo,
            supplierId,
            customerId,
            chatType,
            orderHandlerUserId,
            supplierName: supplierNameResolved,
            customerName: customerNameResolved,
            uniqueName: conv.uniqueName,
            hasUnread: unreadCount > 0,
            unreadCount,
            lastUpdated,
            lastMessageDate,
            lastMessage: conv.lastMessage?.body ?? "",
          };
        } catch (err) {
          console.warn(
            "Skipping conversation due to error:",
            conv.uniqueName,
            err
          );
          return null;
        }
      });

      const chatResults = await Promise.all(chatPromises);
      const validChats = chatResults.filter(
        (chat) => chat !== null
      ) as ChatItem[];

      validChats.sort((a, b) => {
        if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
        if (a.unreadCount !== b.unreadCount)
          return b.unreadCount - a.unreadCount;
        return b.lastUpdated - a.lastUpdated;
      });

      setChats(validChats);
    };

    loadChats();
  }, [
    conversations,
    unreadCounts,
    userDetails._id,
    getSupplierBusinessNameById,
    config.chatType,
    config.behavior,
  ]);

  const handleChatClick = async (uniqueName: string) => {
    const conversation = conversations?.find(
      (conv: any) => conv.uniqueName === uniqueName
    );
    await config.behavior.onChatClick(uniqueName, conversation);
  };

  const hasAnyUnread = hasAnyUnreadForType;

  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const formatTime = (date: Date | undefined) => {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const filteredChats = chats.filter((chat) => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    const byOrder = chat.orderNo.toLowerCase().includes(q);
    const byName = (
      config.chatType === ChatType.SUPPLIER
        ? chat.supplierName
        : chat.customerName
    )
      ?.toLowerCase?.()
      .includes(q);
    return byOrder || byName;
  });

  return (
    <Box
      position="fixed"
      bottom="1px"
      {...(config.position === "left" ? { left: ".5%" } : { right: ".5%" })}
      zIndex={1000}
      display={isConnected ? "block" : "none"}
    >
      <MotionBox
        position="relative"
        width="270px"
        animate={{
          y: 0,
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
      >
        <Box
          as="button"
          onClick={() => setIsMaximized(!isMaximized)}
          bg={hasAnyUnread ? chatStyles.headerBgUnread : chatStyles.headerBg}
          color={
            hasAnyUnread ? chatStyles.headerColorUnread : chatStyles.headerColor
          }
          px={3}
          py={2}
          borderRadius="lg"
          cursor="pointer"
          _hover={{
            bg: hasAnyUnread
              ? chatStyles.headerHoverUnread
              : chatStyles.headerHover,
          }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          borderBottomRadius={isMaximized ? "0" : "lg"}
          borderBottom={isMaximized ? "1px solid" : "none"}
          borderBottomColor={chatStyles.borderBottomColor}
          position="relative"
        >
          <HStack spacing={2} align="center">
            <Text fontSize="sm">{chatStyles.chatTypeIcon}</Text>
            <Text fontWeight="bold" fontSize="md">
              {chatStyles.title}
            </Text>
          </HStack>

          <HStack spacing={2} align="center">
            {hasAnyUnread && (
              <motion.div
                animate={shouldShake ? { x: [0, -3, 3, -3, 3, 0] } : {}}
                transition={{ duration: 0.6 }}
              >
                <Box
                  bg={chatStyles.unreadBadgeBg}
                  color={chatStyles.unreadBadgeColor}
                  borderRadius="full"
                  minW="24px"
                  h="24px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="sm"
                  fontWeight="bold"
                  boxShadow="md"
                  border="2px solid white"
                >
                  {(() => {
                    const unreadConversationsCount = chats.filter(
                      (chat) => chat.hasUnread
                    ).length;
                    return unreadConversationsCount > 99
                      ? "99+"
                      : unreadConversationsCount;
                  })()}
                </Box>
              </motion.div>
            )}

            <IconButton
              aria-label={isMaximized ? "Minimize inbox" : "Expand inbox"}
              icon={isMaximized ? <FaChevronDown /> : <FaChevronUp />}
              size="sm"
              variant="ghost"
              color="inherit"
              _hover={{ bg: "rgba(0,0,0,0.1)" }}
            />
          </HStack>
        </Box>
        <AnimatePresence>
          {isMaximized && (
            <MotionBox
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "560px" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              bg={hasAnyUnread ? "green.50" : "blue.50"}
              borderRadius="0 0 lg lg"
              boxShadow="2xl"
              border="1px solid"
              borderColor={hasAnyUnread ? "green.200" : "blue.200"}
              borderTop="none"
              overflow="hidden"
            >
              <Box
                p={3}
                borderBottom="1px solid"
                borderColor={hasAnyUnread ? "green.200" : "blue.200"}
                bg="white"
              >
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <FaSearch color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder={chatStyles.searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg={hasAnyUnread ? "green.50" : "blue.50"}
                    border="1px solid"
                    borderColor={hasAnyUnread ? "green.200" : "blue.200"}
                    borderRadius="lg"
                    _focus={{
                      bg: "white",
                      borderColor: hasAnyUnread ? "green.400" : "blue.400",
                      boxShadow: hasAnyUnread
                        ? "0 0 0 1px var(--chakra-colors-green-400)"
                        : "0 0 0 1px var(--chakra-colors-blue-400)",
                    }}
                  />
                </InputGroup>
              </Box>

              <Box
                flex={1}
                overflowY="auto"
                bg="white"
                height="calc(600px - 60px)"
              >
                {filteredChats.length === 0 ? (
                  <Box p={8} textAlign="center">
                    <Text color="gray.500" fontSize="sm">
                      {chatStyles.emptyMessage}
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {filteredChats.map((chat) => (
                      <Box
                        key={chat.uniqueName}
                        px={3}
                        py={1}
                        cursor="pointer"
                        bg={chat.hasUnread ? "green.50" : "white"}
                        borderBottom="1px solid"
                        borderColor={chat.hasUnread ? "green.200" : "gray.100"}
                        _hover={{
                          bg: chat.hasUnread ? "green.100" : "gray.100",
                        }}
                        onClick={() => handleChatClick(chat.uniqueName)}
                        position="relative"
                      >
                        <VStack align="stretch" spacing={1}>
                          {/* Single horizontal line: OrderNo, Date, Time, Unread Badge */}
                          <HStack justify="space-between" align="center">
                            <HStack spacing={2} align="center">
                              <Text
                                fontSize="xs"
                                color={
                                  chat.hasUnread
                                    ? "green.500"
                                    : chatStyles.accentColor
                                }
                              >
                                {chatStyles.chatTypeIcon}
                              </Text>
                              <Text
                                fontWeight="bold"
                                fontSize="sm"
                                color="gray.800"
                              >
                                #{chat.orderNo}
                              </Text>
                            </HStack>
                            <HStack spacing={1} align="center">
                              <Text
                                fontSize="2xs"
                                color="gray.500"
                                whiteSpace="nowrap"
                              >
                                {formatDate(chat.lastMessageDate)}
                              </Text>
                              <Text
                                fontSize="2xs"
                                color="gray.500"
                                whiteSpace="nowrap"
                              >
                                {formatTime(chat.lastMessageDate)}
                              </Text>
                            </HStack>
                          </HStack>
                          <HStack
                            spacing={2}
                            align="center"
                            justify={"space-between"}
                          >
                            <HStack spacing={2} align="center">
                              <Text
                                fontSize="xs"
                                color={
                                  chat.hasUnread
                                    ? "green.500"
                                    : chatStyles.accentColor
                                }
                                fontWeight="medium"
                              >
                                {chatStyles.chatTypeLabel}:
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {chat.chatType === ChatType.SUPPLIER
                                  ? chat.supplierName
                                  : chat.customerName}
                              </Text>
                            </HStack>
                            {chat.hasUnread && chat.unreadCount > 0 && (
                              <Box
                                bg={chatStyles.unreadBadgeBg}
                                color={chatStyles.unreadBadgeColor}
                                borderRadius="full"
                                minW="15px"
                                h="15px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="2xs"
                                fontWeight="bold"
                                boxShadow="sm"
                              >
                                {chat.unreadCount > 99
                                  ? "99+"
                                  : chat.unreadCount}
                              </Box>
                            )}
                          </HStack>

                          {/* Last Message */}
                          {chat.lastMessage && (
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              noOfLines={2}
                              lineHeight="1.4"
                            >
                              {chat.lastMessage}
                            </Text>
                          )}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </MotionBox>
    </Box>
  );
};
